import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Download, FileText, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { API } from '../config/api';

export const LEGAL_SLUGS = {
  'mentions-legales': 'Mentions légales',
  'cgv': 'Conditions Générales de Vente',
  'confidentialite': 'Politique de confidentialité',
  'cookies': 'Information cookies',
  'reglement-interieur': 'Règlement intérieur',
  'retractation': 'Droit de rétractation',
};

export default function LegalPage() {
  const { docType } = useParams();
  const { isAuthenticated } = useAuth();
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    setDoc(null); setError('');
    axios.get(`${API}/legal/${docType}`, { withCredentials: true })
      .then(r => setDoc(r.data))
      .catch(() => setError('Cette page n\'est pas encore disponible.'));
    window.scrollTo(0, 0);
  }, [docType]);

  const submitRetractation = async () => {
    setSending(true);
    try {
      const r = await axios.post(`${API}/legal/retractation`, { message });
      toast.success(r.data.message);
      setMessage('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Une erreur est survenue.');
    } finally { setSending(false); }
  };

  if (error) return (
    <div className="min-h-screen bg-[#F5EFE5] flex flex-col items-center justify-center gap-4 text-[#7A6455] px-5 text-center" data-testid="legal-unavailable">
      <p>{error}</p>
      <Link to="/" className="text-sm text-[#C99A55] underline">Retour à l'accueil</Link>
    </div>
  );
  if (!doc) return <div className="min-h-screen bg-[#F5EFE5]" />;

  return (
    <div className="min-h-screen bg-[#F5EFE5] px-5 py-14" data-testid={`legal-page-${docType}`}>
      <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl mx-auto">
        <p className="text-[11px] uppercase tracking-[0.25em] text-[#C99A55] mb-3">PURO Pilates Reformer</p>
        <h1 className="font-heading text-3xl sm:text-4xl text-[#493329] mb-2" data-testid="legal-title">{doc.title}</h1>
        <p className="text-xs text-[#AE9885] mb-8" data-testid="legal-version">
          Version {doc.version} — mise à jour le {new Date(doc.updated_at).toLocaleDateString('fr-FR')}
        </p>

        <div className="bg-white border border-[#E9DDCC] rounded-2xl p-6 sm:p-10 mb-6">
          <div className="whitespace-pre-wrap text-sm leading-7 text-[#5d4a3c]" data-testid="legal-content">{doc.content}</div>
        </div>

        <div className="flex flex-wrap gap-3 mb-10">
          <a href={`${API}/legal/${docType}/pdf`} target="_blank" rel="noopener noreferrer">
            <Button variant="outline" className="rounded-full border-[#C99A55] text-[#9A744C]" data-testid="legal-pdf-btn">
              <Download className="w-4 h-4 mr-2" /> Télécharger en PDF (v{doc.version})
            </Button>
          </a>
          {docType === 'retractation' && (
            <a href={`${API}/legal/retractation-form/pdf`} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="rounded-full border-[#C99A55] text-[#9A744C]" data-testid="retractation-form-pdf-btn">
                <FileText className="w-4 h-4 mr-2" /> Formulaire de rétractation (PDF)
              </Button>
            </a>
          )}
        </div>

        {docType === 'retractation' && (
          <div className="bg-white border border-[#E9DDCC] rounded-2xl p-6 sm:p-8" data-testid="retractation-online-block">
            <h2 className="font-heading text-xl text-[#493329] mb-2">Exercer mon droit de rétractation en ligne</h2>
            {isAuthenticated ? (
              <>
                <p className="text-sm text-[#7A6455] mb-4">Votre demande sera transmise au studio avec la date et l'heure. Vous recevrez une confirmation.</p>
                <Textarea value={message} onChange={e => setMessage(e.target.value)}
                  placeholder="Précisions éventuelles (formule, date de commande…)"
                  className="rounded-xl border-[#E9DDCC] mb-4" data-testid="retractation-message" />
                <Button onClick={submitRetractation} disabled={sending}
                  className="rounded-full bg-[#C99A55] hover:bg-[#9A744C] text-white" data-testid="retractation-submit">
                  <Send className="w-4 h-4 mr-2" /> Envoyer ma demande de rétractation
                </Button>
              </>
            ) : (
              <p className="text-sm text-[#7A6455]">
                <Link to="/login" className="text-[#C99A55] underline">Connectez-vous</Link> pour envoyer votre demande en ligne, ou utilisez le formulaire PDF ci-dessus.
              </p>
            )}
          </div>
        )}

        <div className="mt-10 flex flex-wrap gap-x-5 gap-y-2">
          {Object.entries(LEGAL_SLUGS).filter(([s]) => s !== docType).map(([slug, label]) => (
            <Link key={slug} to={`/legal/${slug}`} className="text-xs text-[#AE9885] hover:text-[#9A744C] underline">{label}</Link>
          ))}
        </div>
      </motion.div>
    </div>
  );
}
